import type { Brand } from '@zg-rentals/zrm-header-navigation';
import Urls from './UrlConstants';

const LISTING_ALIAS_REGEX = '(\\w*[a-zA-Z-][a-zA-Z0-9-]*\\w*|\\d+)';

const ZILLOW_RENTAL_MANAGER = 'Zillow Rental Manager';

const StringConstants = {
  APP_NAME: 'satellite-landlord-web',
  INBOX_PAGE_TITLE: `Messages | ${ZILLOW_RENTAL_MANAGER}`,
  MULTIFAMILY_LEADS_PAGE_TITLE: `Leads | ${ZILLOW_RENTAL_MANAGER}`,
  MULTIFAMILY_LEADS_PAGE_TITLE_FOR_ENTERPRISE_USERS: `Contacts | ${ZILLOW_RENTAL_MANAGER}`,
  LEAD_MANAGEMENT_PAGE_TITLE: `Leads | ${ZILLOW_RENTAL_MANAGER}`,
  NOT_FOUND_PAGE_TITLE: `Page Not Found | ${ZILLOW_RENTAL_MANAGER}`,
  //http method types
  GET: 'get',
  PUT: 'put',
  POST: 'post',
  DELETE: 'delete',

  // direction
  NEXT: 'next',
  PREV: 'prev',

  FORMATTED_BRAND_NAMES: {
    zillow: 'Zillow',
  },

  // using the plain brand names for hotpads and trulia for now whenever we need to use ZRM brand for zillow
  FORMATTED_BRAND_NAMES_RENTAL: {
    zillow: ZILLOW_RENTAL_MANAGER,
  },

  UNFORMATTED_BRAND_NAMES: {
    zillow: 'zillow' as Brand,
  },

  GENERIC_ERROR: 'An error has occurred. Please refresh this page to try again.',
  LEAD_MANAGEMENT_LOAD_MORE_ERROR: 'Unable to load more leads. Please refresh the page to try again.',

  // Canonical error-page copy shared across ZRM apps — must match @zg-rentals/visual-cohesion
  // Storybook verbatim, including the curly apostrophes.
  NOT_FOUND_ERROR_MESSAGE: 'We can’t find that page',
  NOT_FOUND_ERROR_DETAILS: 'That page may have moved or no longer exists. Let’s get you back on track.',
  SERVER_ERROR_MESSAGE: 'Something went wrong on our end',
  SERVER_ERROR_DETAILS: 'Try refreshing the page. If that doesn’t work, check back in a few minutes.',
  GO_TO_RENTAL_MANAGER: 'Go to Rental Manager',
  CONTACT_SUPPORT: 'Contact support',

  // marketing
  MARKETING_PATH: {
    PAYMENTS: 'payments',
    APPLICATIONS: 'applications',
  },

  ALLOW_PARAMS_REGEX: '([?].*)?([&].*)?',

  // server side error string identifier
  // only for BE status code > 399 that are to be fixed in near future
  SS_ERRORS: {
    confirm_phone_number: 'Please confirm the contact phone number.',
  },

  SYSTEM_MESSAGE_ACTION: {
    applicationInvitation: 'View Application',
    newApplicationRequest: 'Send Application',
    resendApplication: 'Resend Application',
  },

  CONVERSATION_PAGE_VIEW_TYPE: {
    ALL: 'all', // Unused for now as this will be default
    PROPERTY: 'property',
  },

  GRAPHQL_ERROR_TYPES: {
    AUTHENTICATION: 'authentication_error',
    AUTHORIZATION: 'authorization_error',
    BAD_REQUEST: 'bad_request_error',
    DEFAULT: 'api_error',
    INTERNAL_SERVER: 'internal_server_error',
    NETWORK: 'network_error',
    RESOURCE_NOT_FOUND: 'resource_not_found',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
  },

  REGEX: {
    // listingAlias to have at least 1 letter
    // listingAlias can either be the feedlistingAlias format ie. 12345_Postlets
    // or aliasEncoded ie. 4z9huhyrzuuru
    // There is an astronomically low probability that aliasEncoded will not contain a non-digit character
    // Probabilty = 13^10 / 13 ^ 33
    // https://stackoverflow.com/questions/33965824/regex-to-match-a-word-with-at-least-one-letter-and-any-number-of-digits-no-look/33965869
    // needs to work with single and double dashes ex: freecr-default-308_LCS, 256341-4957_Buildium
    INBOX_PAGE_PATHNAME: `/:listingAlias${LISTING_ALIAS_REGEX}/:conversationId(\\d+)`,
    LEADS_PAGE_PATHNAME: `/:listingAlias${LISTING_ALIAS_REGEX}?`,
    LISTING_ALIAS_PATHNAME: `/:listingAlias${LISTING_ALIAS_REGEX}?`,
    INQUIRY_ID_PATHNAME: '/:inquiryId(\\d+)',
  },

  TOOLTIP: {
    IMPERSONATION: 'Disabled while impersonating',
    IMPERSONATION_VIEW: 'Cannot view while impersonating',
    REPORTED: 'Cannot reply to conversation that has been reported.',
    PHONE_LEAD: 'To reply to this renter, please call or text their number directly.',
    INVITE_TO_APPLY_MOVE_TO_INBOX: 'Move conversation to inbox in order to send invitation.',
    BLOCKED_BY_ZILLOW: 'Cannot reply to a conversation that has been blocked by Zillow.',
    ENTER_TO_SEND_MESSAGE: 'Send message (Enter/Return)',
  },

  CONVERSATION_BLOCKED_BY_ZILLOW_TEXT:
    'Zillow has blocked this conversation because it was determined to be in violation of our Good Neighbor Policy.',
  CONVERSATION_BLOCKED_BY_ZILLOW_LINK: `${Urls.GOOD_NEIGHBOR_POLICY}`,
  DATE_FNS_FORMAT: 'h:mm a',
  FAILED_SEND_LONG_MESSAGE_TEXT:
    'This message was too long and failed to send. Try splitting it into smaller messages.',
  SENDING_SEND_TEXT: 'Sending...',

  SCREENING_CRITERIA_NOT_ENABLED:
    'Screening tenants for this property address is restricted due to the local and/or federal laws',

  SCREENING_CRITERIA_LOAD_ERROR:
    "We're not able to display your screening criteria data at the moment. Please refresh the page and try again.",

  SCREENING_CRITERIA_SAVE_ERROR: 'Failed to save. Please try again.',

  // Filters and Folders
  ACTIONS: {
    ARCHIVE: 'archive',
    SPAM: 'spam',
    UNARCHIVE: 'unarchive',
    UNSPAM: 'unspam',
    MARK_UNREAD: 'Mark unread',
    NOT_SPAM: 'Not spam',
    MOVE_TO_INBOX: 'Move to inbox',
  },

  MOBILE_PLATFORMS: {
    IOS: 'IOS',
    ANDROID: 'Android',
  },

  START_CONVERSATION_TEXT: 'This is the start of your conversation',

  VIEW_IN_CALENDAR: 'View in Calendar',

  VIEW_IN_MESSAGES: 'View in messages',

  LISTINGS_COMBOBOX_PLACEHOLDER: 'All listings',
  LISTINGS_COMBOBOX_LOADING: 'Loading properties...',

  // Empty states
  LEADS_EMPTY_STATE_HEADING: 'No leads yet',
  LEADS_EMPTY_STATE_BODY:
    'Your leads will show up here when renters reach out. Check that your listing is published and up to date.',
  LEADS_NO_PROPERTIES_BODY: 'Get started by adding your first rental property.',
  INBOX_SIDEBAR_EMPTY_HEADING: 'See more information on a potential renter here',

  // Attachment upload errors, keyed by cause so each toast names the corrective action.
  ATTACHMENT_UPLOAD_ERROR: {
    INFRA: 'File couldn’t be uploaded. Refresh the page and try again.',
    INVALID_TYPE: 'File couldn’t be uploaded. Use a supported file and try again.',
    TOO_LARGE: 'File couldn’t be uploaded. Keep files under 20 MB and try again.',
    UNKNOWN: 'File couldn’t be uploaded. Please try again.',
  },
};

export default StringConstants;
